import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const NGOSettings: React.FC = () => {
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    legal_name: '',
    registration_type: '',
    pan: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    contact: {
      name: '',
      email: '',
      phone: ''
    },
    bank_details: {
      account_number: '',
      ifsc: '',
      bank_name: '',
      branch: ''
    },
    documents: {
      csr1: null as File | null,
      '12a': null as File | null,
      '80g': null as File | null,
      fcra: null as File | null
    },
    logo: null as File | null,
    logo_url: null as string | null
  });

  useEffect(() => {
    if (ngoId) {
      loadNGOData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ngoId]);

  const loadNGOData = async () => {
    if (!ngoId) return;

    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('ngos')
        .select('*')
        .eq('id', ngoId)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          legal_name: data.legal_name || '',
          registration_type: data.registration_type || '',
          pan: data.pan || '',
          address: data.address || { street: '', city: '', state: '', pincode: '', country: 'India' },
          contact: data.contact || { name: '', email: '', phone: '' },
          bank_details: data.bank_details || { account_number: '', ifsc: '', bank_name: '', branch: '' },
          documents: { csr1: null, '12a': null, '80g': null, fcra: null },
          logo: null,
          logo_url: data.logo_url || null
        });

        if (data.logo_url) {
          let path = data.logo_url;
          if (path.includes('/object/public/ngo-documents/')) {
            path = path.split('/object/public/ngo-documents/')[1].split('?')[0];
          } else if (path.includes('/ngo-documents/')) {
            path = path.split('/ngo-documents/')[1].split('?')[0];
          }
          if (path) {
            const { data: signedData } = await supabase.storage.from('ngo-documents').createSignedUrl(decodeURIComponent(path), 3600);
            if (signedData?.signedUrl) setLogoPreviewUrl(signedData.signedUrl);
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load NGO data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (name.startsWith('contact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        contact: { ...prev.contact, [field]: value }
      }));
    } else if (name.startsWith('bank_details.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bank_details: { ...prev.bank_details, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (docType: string, file: File | null) => {
    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [docType]: file }
    }));
  };

  const uploadDocument = async (file: File, docType: string): Promise<string> => {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${ngoId}/${docType}-${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from('ngo-documents').upload(fileName, file);
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('ngo-documents').getPublicUrl(fileName);
    return publicUrl;
  };

  const saveDraft = async () => {
    if (!ngoId) return;
    setSavingDraft(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('ngos')
        .update({
          legal_name: formData.legal_name,
          registration_type: formData.registration_type,
          pan: formData.pan,
          address: formData.address,
          contact: formData.contact,
          bank_details: formData.bank_details,
          profile_complete: false
        })
        .eq('id', ngoId);

      if (updateError) throw updateError;

      alert('Draft saved successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Manual Validation
    const requiredFields = [
      { key: 'legal_name', label: 'Legal Name' },
      { key: 'registration_type', label: 'Registration Type' },
      { key: 'address.street', label: 'Street Address', value: formData.address.street },
      { key: 'address.city', label: 'City', value: formData.address.city },
      { key: 'address.state', label: 'State', value: formData.address.state },
      { key: 'address.pincode', label: 'Pincode', value: formData.address.pincode },
      { key: 'address.country', label: 'Country', value: formData.address.country },
      { key: 'contact.name', label: 'Contact Name', value: formData.contact.name },
      { key: 'contact.email', label: 'Contact Email', value: formData.contact.email },
      { key: 'contact.phone', label: 'Contact Phone', value: formData.contact.phone }
    ];

    for (const field of requiredFields) {
      const value = field.value !== undefined ? field.value : (formData as any)[field.key];
      if (!value || (typeof value === 'string' && !value.trim())) {
        setError(`${field.label} is required`);
        setLoading(false);
        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    try {
      if (!ngoId) throw new Error('NGO ID not found');

      // Upload logo if changed
      let logoUrl = formData.logo_url;
      if (formData.logo) {
        logoUrl = await uploadDocument(formData.logo, 'logo');
      }

      // Upload new documents if any
      for (const [docType, file] of Object.entries(formData.documents)) {
        if (file) {
          const url = await uploadDocument(file, docType);
          await supabase.from('ngo_documents').insert({
            ngo_id: ngoId,
            document_type: docType === '12a' ? '12a' : docType === '80g' ? '80g' : docType === 'fcra' ? 'fcra' : 'csr1',
            file_url: url,
            file_name: (file as File).name
          });
        }
      }

      const { error: updateError } = await supabase
        .from('ngos')
        .update({
          legal_name: formData.legal_name,
          registration_type: formData.registration_type,
          pan: formData.pan,
          address: formData.address,
          contact: formData.contact,
          bank_details: formData.bank_details,
          logo_url: logoUrl,
          status: 'submitted',
          profile_complete: false
        })
        .eq('id', ngoId);

      if (updateError) throw updateError;

      // Refresh logo preview after save
      if (formData.logo && logoUrl) {
        let path = logoUrl;
        if (path.includes('/object/public/ngo-documents/')) path = path.split('/object/public/ngo-documents/')[1].split('?')[0];
        else if (path.includes('/ngo-documents/')) path = path.split('/ngo-documents/')[1].split('?')[0];
        if (path) {
          const { data: signedData } = await supabase.storage.from('ngo-documents').createSignedUrl(decodeURIComponent(path), 3600);
          if (signedData?.signedUrl) setLogoPreviewUrl(signedData.signedUrl);
        }
        setFormData(prev => ({ ...prev, logo_url: logoUrl, logo: null }));
        if (logoFileInputRef.current) logoFileInputRef.current.value = '';
      }

      alert('Profile updated and submitted for verification!');
      navigate(`/ngo/ngo/dashboard`);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Edit NGO Profile</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Update your NGO profile information
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <h2>Basic Information</h2>

          {/* NGO Logo */}
          <div className="form-group">
            <label>NGO Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
              {(logoPreviewUrl || formData.logo_url) && !formData.logo && (
                <img
                  src={logoPreviewUrl || formData.logo_url!}
                  alt="Current logo"
                  style={{ height: '60px', maxWidth: '150px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border-light)' }}
                />
              )}
              {formData.logo && (
                <img
                  src={URL.createObjectURL(formData.logo)}
                  alt="New logo preview"
                  style={{ height: '60px', maxWidth: '150px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--border-light)' }}
                />
              )}
              <div style={{ flex: 1 }}>
                <input
                  type="file"
                  ref={logoFileInputRef}
                  accept="image/*"
                  onChange={(e) => setFormData(prev => ({ ...prev, logo: e.target.files?.[0] || null }))}
                  className="file-input"
                />
                {formData.logo && (
                  <p style={{ fontSize: '0.8rem', color: '#10b981', marginTop: '0.25rem' }}>✓ New selection: {formData.logo.name}</p>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Legal Name *</label>
            <input
              type="text"
              name="legal_name"
              value={formData.legal_name}
              onChange={handleChange}
              required
              placeholder="Registered legal name"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Registration Type *</label>
              <select
                name="registration_type"
                value={formData.registration_type}
                onChange={handleChange}
                required
              >
                <option value="">Select type</option>
                <option value="society">Society</option>
                <option value="trust">Trust</option>
                <option value="section_8">Section 8 Company</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>PAN</label>
              <input
                type="text"
                name="pan"
                value={formData.pan}
                onChange={handleChange}
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </div>
          </div>

          <h2 style={{ marginTop: '2rem' }}>Address</h2>

          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pincode *</label>
              <input
                type="text"
                name="address.pincode"
                value={formData.address.pincode}
                onChange={handleChange}
                required
                pattern="[0-9]{6}"
                maxLength={6}
              />
            </div>

            <div className="form-group">
              <label>Country *</label>
              <input
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <h2 style={{ marginTop: '2rem' }}>Contact Information</h2>

          <div className="form-group">
            <label>Contact Name *</label>
            <input
              type="text"
              name="contact.name"
              value={formData.contact.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="contact.email"
                value={formData.contact.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                name="contact.phone"
                value={formData.contact.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <h2 style={{ marginTop: '2rem' }}>Bank Details</h2>

          <div className="form-group">
            <label>Account Number</label>
            <input
              type="text"
              name="bank_details.account_number"
              value={formData.bank_details.account_number}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>IFSC Code</label>
              <input
                type="text"
                name="bank_details.ifsc"
                value={formData.bank_details.ifsc}
                onChange={handleChange}
                placeholder="ABCD0123456"
              />
            </div>

            <div className="form-group">
              <label>Bank Name</label>
              <input
                type="text"
                name="bank_details.bank_name"
                value={formData.bank_details.bank_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Branch</label>
            <input
              type="text"
              name="bank_details.branch"
              value={formData.bank_details.branch}
              onChange={handleChange}
            />
          </div>

          <h2 style={{ marginTop: '2rem' }}>Certificates & Documents</h2>
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Upload new documents to replace existing ones (optional)
          </p>

          <div className="form-group">
            <label>CSR-1 Certificate</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange('csr1', e.target.files?.[0] || null)}
            />
            <div className="help-text">Optional - will replace existing document</div>
          </div>

          <div className="form-group">
            <label>12A Certificate</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange('12a', e.target.files?.[0] || null)}
            />
            <div className="help-text">Optional</div>
          </div>

          <div className="form-group">
            <label>80G Certificate</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange('80g', e.target.files?.[0] || null)}
            />
            <div className="help-text">Optional</div>
          </div>

          <div className="form-group">
            <label>FCRA Certificate</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange('fcra', e.target.files?.[0] || null)}
            />
            <div className="help-text">Optional (if applicable)</div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={saveDraft}
              disabled={savingDraft}
            >
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit for Verification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NGOSettings;

